import { adminCurrencyFormatter } from '@diamond/shared/utils';

import {
  AdminFinalizedPaymentProofItem,
  AdminPaymentProofDisplayItem,
  AdminPaymentProofOcrItem,
} from '../types/payment-proof-ocr';

export const MAX_PAYMENT_PROOF_OCR_ITEMS = 5;
const EMPTY_FIELD = '-';

function getProofPathname(url: string): string {
  try {
    return new URL(url).pathname.toLowerCase();
  } catch {
    return url.split('?')[0].toLowerCase();
  }
}

export function isPaymentProofPdf(url: string): boolean {
  return getProofPathname(url).endsWith('.pdf');
}

type AdminFinalizedPaymentProofWithOcr = AdminFinalizedPaymentProofItem & {
  admin_display: NonNullable<AdminFinalizedPaymentProofItem['admin_display']>;
  ocr: NonNullable<AdminFinalizedPaymentProofItem['ocr']>;
};

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const shortDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: '2-digit',
});
const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function parseDate(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function formatDisplayDateTime(value: string | null): string {
  const date = parseDate(value);

  if (!date) {
    return EMPTY_FIELD;
  }

  return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
}

function formatUploadedAt(value: string): string {
  const date = parseDate(value);

  if (!date) {
    return EMPTY_FIELD;
  }

  return `${timeFormatter.format(date)} • ${shortDateFormatter.format(date)}`;
}

function formatAmount(amount: number | null): string {
  if (amount === null) {
    return EMPTY_FIELD;
  }

  return adminCurrencyFormatter(amount);
}

function hasAdminOcrDisplay(
  paymentProof: AdminFinalizedPaymentProofItem
): paymentProof is AdminFinalizedPaymentProofWithOcr {
  return paymentProof.ocr !== null && paymentProof.admin_display !== null;
}

function toPaymentProofOcrItem(
  paymentProof: AdminFinalizedPaymentProofWithOcr
): AdminPaymentProofOcrItem {
  return {
    id: paymentProof.id,
    url: paymentProof.url,
    status: paymentProof.admin_display.state,
    amount: formatAmount(paymentProof.ocr.amount),
    recipientName: paymentProof.ocr.recipient_name ?? EMPTY_FIELD,
    transactionDatetime: formatDisplayDateTime(
      paymentProof.ocr.transaction_datetime
    ),
    senderName: paymentProof.ocr.sender_account_name ?? EMPTY_FIELD,
    senderBank: paymentProof.ocr.sender_bank ?? EMPTY_FIELD,
    uploadedAt: formatUploadedAt(paymentProof.created_at),
    warnings: paymentProof.admin_display.warning_labels,
  };
}

export function getPaymentProofOcrItems(
  paymentProofs: AdminFinalizedPaymentProofItem[]
): AdminPaymentProofOcrItem[] {
  return paymentProofs
    .filter(hasAdminOcrDisplay)
    .slice(0, MAX_PAYMENT_PROOF_OCR_ITEMS)
    .map(toPaymentProofOcrItem);
}

export function getPaymentProofDisplayItems(
  paymentProofs: AdminFinalizedPaymentProofItem[]
): AdminPaymentProofDisplayItem[] {
  return paymentProofs.slice(0, MAX_PAYMENT_PROOF_OCR_ITEMS).map((proof) => {
    if (isPaymentProofPdf(proof.url)) {
      return {
        kind: 'pdf',
        id: proof.id,
        url: proof.url,
      };
    }

    if (hasAdminOcrDisplay(proof)) {
      return {
        kind: 'ocr',
        item: toPaymentProofOcrItem(proof),
      };
    }

    return {
      kind: 'legacy',
      id: proof.id,
      url: proof.url,
    };
  });
}

export function getLegacyPaymentProofDisplayItems(
  proofUrls: string[]
): AdminPaymentProofDisplayItem[] {
  return proofUrls.slice(0, MAX_PAYMENT_PROOF_OCR_ITEMS).map((url, index) => {
    const id = `${index}-${url}`;

    if (isPaymentProofPdf(url)) {
      return {
        kind: 'pdf',
        id,
        url,
      };
    }

    return {
      kind: 'legacy',
      id,
      url,
    };
  });
}
